import { buildParentLookup, isQuestionVisible } from "@idwal/checklist-visibility";
import { ChecklistConfig, Question, QuestionOption } from "../types/checklist";
import { ChecklistReviewRow, ReviewAnswer } from "../types/reviewAnswer";
import { Answer } from "../types/answer";
import { getAnswerDisplayValue } from "../components/Shared/AnswerEditor";

/**
 * Mirror of the backend's normaliseQuestionName (syncAnswers / pdf-survey-parser):
 * the "inspection" table stores `_details`/`_input` fields under a
 * `_comment` suffix. To match a checklist question to its answer we must
 * apply the same normalisation to the question's dataDictionaryId.
 */
export const normaliseQuestionName = (dataDictionaryId?: string): string => {
    if (!dataDictionaryId) return "";
    return dataDictionaryId
        .replace(/_details$/, "_comment")
        .replace(/_input$/, "_comment");
};

/**
 * Loose comparison used to map a display value back to a checklist  option. 
 * The table stores choice answers as the capitalised option id
 * (e.g. "Access_not_permitted_vessel") or, for multi-choice, with underscores
 * replaced by spaces. Normalising case and separators lets us match against
 * either the option id or its display text without depending on the exact
 * formatting the backend applied.
 */
const normaliseForCompare = (value: string): string =>
    value.trim().toLowerCase().replace(/[\s_]+/g, "_");

/** Find the checklist option a value corresponds to (by id or text). */
const matchOption = (value: string, options?: QuestionOption[]): QuestionOption | undefined => {
    if (!options) return undefined;
    const target = normaliseForCompare(value);
    return options.find(
        (option) =>
            normaliseForCompare(option.id) === target ||
            normaliseForCompare(option.text) === target
    );
};

/**
 * Resolve the human-readable Response string for one checklist question from
 * the review answers, indexed by questionName.
 *
 * Returns "" when the question is unanswered
 */
const resolveResponse = (
    question: Question,
    answersByName: Map<string, ReviewAnswer>
): string => {
    // Multi-choice answers are stored as one row per selected option, keyed
    // `${dataDictionaryId}_${optionId}`. Collect the selected options in the
    // checklist's own option order so the display is stable.
    if (question.type === "multi-choice") {
        const selected = (question.options ?? [])
            .filter((option) =>
                answersByName.has(`${question.dataDictionaryId}_${option.id}`)
            )
            .map((option) => option.text);
        return selected.join(", ");
    }

    const answer = answersByName.get(normaliseQuestionName(question.dataDictionaryId));
    if (!answer || answer.value === null || answer.value === "") {
        return "";
    }

    const value = String(answer.value);

    switch (question.type) {
        case "single-choice":
        case "boolean": {
            // Prefer the polished checklist option text over the
            // capitalised-id form; fall back to the raw value if no match.
            return matchOption(value, question.options)?.text ?? value;
        }
        case "number": {
            return question.unit ? `${value} ${question.unit}` : value;
        }
        default:
            return value;
    }
};

/**
 * Resolve the editor-ready raw value for one checklist question — the value
 * used to seed the inline editor, distinct from the formatted display
 * `response`. Returns null when the question is unanswered.
 *
 * single-choice → option id; boolean → true/false; number → number; text/date
 * → the raw string; multi-choice → the selected option ids.
 */
const resolveEditorValue = (
    question: Question,
    answersByName: Map<string, ReviewAnswer>
): unknown => {
    if (question.type === "multi-choice") {
        return (question.options ?? [])
            .filter((option) => answersByName.has(`${question.dataDictionaryId}_${option.id}`))
            .map((option) => option.id);
    }

    const answer = answersByName.get(normaliseQuestionName(question.dataDictionaryId));
    if (!answer || answer.value === null || answer.value === "") {
        return null;
    }

    const value = String(answer.value);

    switch (question.type) {
        case "single-choice":
            return matchOption(value, question.options)?.id ?? value;
        case "boolean":
            // Inspection table stores "Yes"/"No"; the boolean editor expects true/false.
            if (/^(yes|true)$/i.test(value)) return true;
            if (/^(no|false)$/i.test(value)) return false;
            return matchOption(value, question.options)?.id ?? value;
        case "number": {
            const parsed = parseFloat(value);
            return Number.isNaN(parsed) ? value : parsed;
        }
        default:
            return value;
    }
};

/**
 * Format an edited value the way the backend stores it in the inspection table, so
 * an optimistic update displays identically to what a later refetch returns.
 */
export const formatReviewAnswerValue = (value: unknown): string => {
    if (value === null || value === undefined) return "";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed && !trimmed.includes(" ")) {
            return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
        }
        return value;
    }
    return String(value);
};

export interface VisibilityInputs {
    /** Every checklist question, flattened in document order. */
    allQuestions: Question[];
    /** Child question id → parent question id (from conditionalQuestions). */
    parentLookup: Map<string, string>;
    /** Id-keyed answer map in the value form @idwal/checklist-visibility expects
     *  (option ids, booleans, numbers, arrays); only answered questions appear. */
    answersById: Record<string, { value: unknown }>;
}

/**
 * Inputs for @idwal/checklist-visibility, derived from the review answers.
 * resolveEditorValue yields the value form the package expects, so a question is
 * shown only when its parent chain's answers trigger it. Shared by buildReviewRows
 * and the orphan-clear logic so both derive visibility identically.
 */
export const buildVisibilityInputs = (
    config: ChecklistConfig,
    answersByName: Map<string, ReviewAnswer>
): VisibilityInputs => {
    const allQuestions: Question[] = config.sections.flatMap(
        (section) => section.subsections.flatMap((subsection) => subsection.questions)
    );
    const parentLookup = buildParentLookup(allQuestions);
    const answersById: Record<string, { value: unknown }> = {};
    for (const question of allQuestions) {
        const value = resolveEditorValue(question, answersByName);
        if (value !== null && value !== undefined) {
            answersById[question.id] = { value };
        }
    }
    return { allQuestions, parentLookup, answersById };
};

/**
 * The review-answer questionNames belonging to a set of checklist questions:
 * the value row (the normalised dataDictionaryId) plus, for multi-choice, the
 * per-option rows (`${dataDictionaryId}_${optionId}`). Used to hard-delete
 * orphaned descendant answers when a parent edit hides their branch.
 */
export const questionNamesForQuestions = (
    questions: Question[],
    reviewAnswers: ReviewAnswer[]
): string[] => {
    const names = new Set<string>();
    for (const question of questions) {
        const dataDictionaryId = question.dataDictionaryId;
        if (!dataDictionaryId) continue;
        const normalised = normaliseQuestionName(dataDictionaryId);
        // Gate the prefix match on multi-choice so a question whose
        // dataDictionaryId is a prefix of another's can't sweep it up.
        const optionPrefix = question.type === "multi-choice" ? `${dataDictionaryId}_` : null;
        for (const answer of reviewAnswers) {
            if (answer.questionName === normalised
                || (optionPrefix !== null && answer.questionName.startsWith(optionPrefix))) {
                names.add(answer.questionName);
            }
        }
    }
    return Array.from(names);
};

/**
 * Build the Checklist Review table rows by joining the checklist configuration
 * with the review answers.
 *
 * The checklist drives the output: one row per question in section → subsection
 * → question order, with unanswered questions included as a blank Response.
 */
export const buildReviewRows = (
    config: ChecklistConfig | null,
    reviewAnswers: ReviewAnswer[]
): ChecklistReviewRow[] => {
    if (!config) return [];

    const answersByName = new Map<string, ReviewAnswer>();
    for (const answer of reviewAnswers) {
        answersByName.set(answer.questionName, answer);
    }

    const { allQuestions, parentLookup, answersById } = buildVisibilityInputs(config, answersByName);

    const rows: ChecklistReviewRow[] = [];
    const sections = [...config.sections].sort((a, b) => a.order - b.order);

    for (const section of sections) {
        for (const subsection of section.subsections) {
            for (const question of subsection.questions) {
                if (!isQuestionVisible(question, allQuestions, answersById, parentLookup)) {
                    continue;
                }
                const answer = answersByName.get(
                    normaliseQuestionName(question.dataDictionaryId)
                );
                rows.push({
                    questionId: question.id,
                    sectionTitle: section.title,
                    subsectionTitle: subsection.title,
                    questionText: question.question,
                    response: resolveResponse(question, answersByName),
                    rawValue: resolveEditorValue(question, answersByName),
                    questionType: question.type,
                    dataDictionaryId: question.dataDictionaryId ?? null,
                    isNotable: answer?.isNotable ?? false,
                });
            }
        }
    }

    return rows;
};

/**
 * Build the Surveyor Submission table rows by joining the checklist
 * configuration with the surveyor's original answers from the answers table.
 *
 * Unlike the review answers (inspection table, keyed by normalised
 * dataDictionaryId), the answers table keys rows by the checklist question id
 * directly and stores raw values (booleans, option ids, arrays), so the join
 * is exact and display formatting is delegated to getAnswerDisplayValue.
 * Same output contract as buildReviewRows: conditional visibility applied (only
 * the questions the surveyor was shown), unanswered visible ones blank.
 */
export const buildSurveyorRows = (
    config: ChecklistConfig | null,
    answers: Answer[]
): ChecklistReviewRow[] => {
    if (!config) return [];

    const answersByQuestionId = new Map<string, Answer>();
    for (const answer of answers) {
        answersByQuestionId.set(answer.questionId, answer);
    }

    // Apply the conditional logic the surveyor saw: show a question only when its
    // parent chain's answers trigger it
    const allQuestions: Question[] = config.sections.flatMap(
        (section) => section.subsections.flatMap((subsection) => subsection.questions)
    );
    const parentLookup = buildParentLookup(allQuestions);
    const answersById: Record<string, { value: unknown }> = {};
    for (const question of allQuestions) {
        const value = answersByQuestionId.get(question.id)?.value;
        if (value !== null && value !== undefined && value !== "") {
            answersById[question.id] = { value };
        }
    }

    const rows: ChecklistReviewRow[] = [];
    const sections = [...config.sections].sort((a, b) => a.order - b.order);

    for (const section of sections) {
        for (const subsection of section.subsections) {
            for (const question of subsection.questions) {
                if (!isQuestionVisible(question, allQuestions, answersById, parentLookup)) {
                    continue;
                }
                const answer = answersByQuestionId.get(question.id);
                rows.push({
                    questionId: question.id,
                    sectionTitle: section.title,
                    subsectionTitle: subsection.title,
                    questionText: question.question,
                    response: answer ? getAnswerDisplayValue(answer.value, question) : "",
                    // The surveyor submission is read-only; rawValue is carried
                    // only for type parity with the editable review rows.
                    rawValue: answer ? answer.value : null,
                    questionType: question.type,
                    dataDictionaryId: question.dataDictionaryId ?? null,
                    isNotable: answer?.isNotable ?? false,
                });
            }
        }
    }

    return rows;
};
